// Single chat message bubble, sent or received.

import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Drafty, Tinode } from 'tinode-sdk';

import Attachment from './attachment.jsx';
import LetterTile from './letter-tile.jsx';
import ReceivedMarker from './received-marker.jsx'

import { emojiOnlyCount } from '../lib/emoji.js';
import { fullFormatter } from '../lib/formatters.js';
import { sanitizeUrl } from '../lib/utils.js';

class BaseChatMessage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      progress: 0,
      // Emoji quick-picker visibility.
      picker: false,
      // Seconds remaining before a revealed self-destructing message disappears.
      remaining: props.timerState ? props.timerState.remaining : 0
    };

    this.countdown = null;

    if (props.uploader) {
      props.uploader.onProgress = this.handleProgress.bind(this);
    }

    this.handleTogglePicker = this.handleTogglePicker.bind(this);
    this.handlePickReaction = this.handlePickReaction.bind(this);
    this.handleToggleReaction = this.handleToggleReaction.bind(this);
    this.handleRevealTimed = this.handleRevealTimed.bind(this);
    this.handleRetrySend = this.handleRetrySend.bind(this);
    this.handleDiscardSend = this.handleDiscardSend.bind(this);
    this.handleExpandImage = this.handleExpandImage.bind(this);
    this.handlePlayVideo = this.handlePlayVideo.bind(this);
    this.handleFormButtonClick = this.handleFormButtonClick.bind(this);
    this.handleContextClick = this.handleContextClick.bind(this);
    this.handleCancelUpload = this.handleCancelUpload.bind(this);
    this.handleQuoteClick = this.handleQuoteClick.bind(this);

    this.formatterContext = {
      formatMessage: props.intl.formatMessage.bind(props.intl),
      viewportWidth: props.viewportWidth,
      authorizeURL: props.tinode.authorizeURL.bind(props.tinode),

      onImagePreview: this.handleExpandImage,
      onVideoPreview: this.handlePlayVideo,
      onFormButtonClick: this.handleFormButtonClick,
      onQuoteClick: this.handleQuoteClick
    };
  }

  componentDidMount() {
    this.maybeStartCountdown();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.timerState !== this.props.timerState) {
      this.setState({remaining: this.props.timerState ? this.props.timerState.remaining : 0});
      this.maybeStartCountdown();
    }
  }

  componentWillUnmount() {
    if (this.countdown) {
      clearInterval(this.countdown);
      this.countdown = null;
    }
  }

  // Tick down a revealed self-destructing message and hide it when the time is up.
  maybeStartCountdown() {
    if (this.countdown) {
      clearInterval(this.countdown);
      this.countdown = null;
    }
    const ts = this.props.timerState;
    if (!ts || ts.sender || !isFinite(ts.remaining)) {
      return;
    }
    this.countdown = setInterval(_ => {
      const left = this.state.remaining - 1;
      if (left <= 0) {
        clearInterval(this.countdown);
        this.countdown = null;
        this.setState({remaining: 0});
        if (this.props.onTimerExpired) {
          this.props.onTimerExpired(this.props.seq);
        }
      } else {
        this.setState({remaining: left});
      }
    }, 1000);
  }

  handleTogglePicker(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState(prev => ({picker: !prev.picker}));
  }

  handlePickReaction(emoji, e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({picker: false});
    if (this.props.onToggleReaction) {
      this.props.onToggleReaction(this.props.seq, emoji);
    }
  }

  handleToggleReaction(emoji, e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.props.onToggleReaction) {
      this.props.onToggleReaction(this.props.seq, emoji);
    }
  }

  handleRevealTimed(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.props.onRevealTimed) {
      this.props.onRevealTimed(this.props.seq, this.props.timer, !this.props.response);
    }
  }

  handleRetrySend(e) {
    e.preventDefault();
    if (this.props.onRetrySend) {
      this.props.onRetrySend(this.props.seq);
    }
  }

  handleDiscardSend(e) {
    e.preventDefault();
    if (this.props.onDiscardSend) {
      this.props.onDiscardSend(this.props.seq);
    }
  }

  handleExpandImage(e) {
    e.preventDefault();
    this.props.onExpandMedia({
      url: e.target.src,
      filename: e.target.dataset.name,
      width: e.target.dataset.width,
      height: e.target.dataset.height,
      size: e.target.dataset.size,
      type: e.target.dataset.mime
    });
  }

  handlePlayVideo(e) {
    e.preventDefault();
    this.props.onExpandMedia({
      video: true,
      url: e.target.dataset.src,
      preview: e.target.src,
      filename: e.target.dataset.name,
      width: e.target.dataset.width,
      height: e.target.dataset.height,
      duration: e.target.dataset.duration,
      size: e.target.dataset.size,
      type: e.target.dataset.mime
    });
  }

  handleFormButtonClick(e) {
    e.preventDefault();
    const data = {
      seq: this.props.seq
    };
    data.resp = {};
    if (e.target.dataset.name) {
      data.resp[e.target.dataset.name] = e.target.dataset.val ? e.target.dataset.val :
        e.target.dataset.val === undefined ? 1 : '' + e.target.dataset.val;
    }
    if (e.target.dataset.act == 'url') {
      data.ref = sanitizeUrl(e.target.dataset.ref) || 'about:blank';
    }
    const text = e.target.dataset.title || 'unknown';
    this.props.onFormResponse(e.target.dataset.act, text, data);
  }

  handleContextClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const menuItems = [];
    if (this.props.received == Tinode.MESSAGE_STATUS_FAILED) {
      menuItems.push('menu_item_send_retry');
    }
    if (this.props.received > Tinode.MESSAGE_STATUS_FATAL) {
      if (this.props.userIsWriter) {
        menuItems.push('menu_item_reply');
        if (!this.props.response) {
          let immutable = false;
          Drafty.entities(this.props.content, (_0, _1, tp) => {
            immutable = ['AU', 'EX', 'FM', 'IM', 'VC', 'VD'].includes(tp);
            return immutable;
          });
          if (!immutable) {
            Drafty.styles(this.props.content, tp => {
              immutable = ['QQ'].includes(tp);
              return immutable;
            });
          }
          if (!immutable) {
            menuItems.push('menu_item_edit');
          }
        }
      }
      if (this.props.userIsAdmin) {
        menuItems.push(this.props.pinned ? 'menu_item_unpin' : 'menu_item_pin');
      }
    }
    menuItems.push('menu_item_forward');

    this.props.showContextMenu({
      seq: this.props.seq,
      replace: this.props.edited ? parseInt(this.props.edited.split(':')[1]) : 0,
      content: this.props.content,
      userFrom: this.props.userFrom,
      userName: this.props.userName,
      y: e.pageY,
      x: e.pageX,
      pickReply: this.props.pickReply,
      editMessage: this.props.editMessage,
      timestamp: this.props.timestamp,
    }, menuItems);
  }

  handleProgress(ratio) {
    this.setState({progress: ratio});
  }

  handleCancelUpload() {
    this.props.onCancelUpload(this.props.seq, this.props.uploader);
  }

  handleQuoteClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const replyToSeq = this.props.replyToSeq;
    if (replyToSeq) {
      this.props.onQuoteClick(replyToSeq);
    }
  }

  render() {
    const sideClass = this.props.sequence + ' ' + (this.props.response ? 'left' : 'right');
    const bubbleClass = (this.props.sequence == 'single' || this.props.sequence == 'last') ? 'bubble tip' : 'bubble';
    const avatar = this.props.userAvatar || true;
    let textSizeClass = 'message-content';
    const fullDisplay = (this.props.isGroup && this.props.response &&
      (this.props.sequence == 'single' || this.props.sequence == 'last'));

    let content = this.props.content;
    const attachments = [];
    if (this.props.mimeType == Drafty.getContentType() && Drafty.isValid(content)) {
      Drafty.attachments(content, (att, i) => {
        if (Drafty.isFormResponseType(att.mime)) {
          // Don't show json drafty form response objects as attachments.
          // They are not meant for users.
          return;
        }
        attachments.push(<Attachment
          tinode={this.props.tinode}
          downloadUrl={Drafty.getDownloadUrl(att)}
          filename={att.name}
          uploading={Drafty.isProcessing(att)}
          mimetype={att.mime}
          entityData={att}
          size={Drafty.getEntitySize(att)}
          progress={this.state.progress}
          onCancelUpload={this.handleCancelUpload}
          onError={this.props.onError}
          key={i} />);
      }, this);
      // Make font bigger for emoji-only messages, same as a plain-text one.
      // iOS enlarges emoji-only messages whether or not they arrive as Drafty,
      // so unstyled Drafty text has to get the same treatment here.
      const bare = Drafty.isPlainText(content) ? Drafty.toPlainText(content) : null;
      const n = emojiOnlyCount(bare);
      if (n > 0) {
        textSizeClass += ' emoji-' + n;
      }
      const tree = Drafty.format(content, fullFormatter, this.formatterContext);
      content = React.createElement(React.Fragment, null, tree);
    } else if (typeof content == 'string') {
      // Make font bigger for emoji-only messages. emojiOnlyCount() builds the
      // \p{RGI_Emoji} regex once and defensively: the ES2024 'v' flag throws a
      // SyntaxError on older engines, and this runs inside render() for every
      // message, so an unguarded construction takes down the whole message list.
      const n = emojiOnlyCount(content);
      if (n > 0) {
        textSizeClass += ' emoji-' + n;
      }
    } else {
      content = <><i className="material-icons gray">warning_amber</i> <i className="gray">
        <FormattedMessage id="invalid_content"
          defaultMessage="invalid content" description="Shown when the message is unreadable" /></i></>
    }

    // Self-destructing ("timer") message: a recipient must tap to reveal it, after which
    // it is visible for `timer` seconds only. Mirrors the iOS behaviour. NOTE: the server
    // does not delete these messages, this is client-side hygiene, not a guarantee.
    const isSender = !this.props.response;
    let timerBadge = null;
    if (this.props.timer > 0) {
      if (!this.props.timerState) {
        if (isSender) {
          // The sender always sees their own timed message.
          timerBadge = <span className="timer-badge"><i className="material-icons">timer</i>
            {this.props.timer}s</span>;
        } else {
          content = (
            <a href="#" className="timed-message-veil" onClick={this.handleRevealTimed}>
              <i className="material-icons">visibility</i>&nbsp;
              <FormattedMessage id="timed_message_tap_to_view"
                defaultMessage="Tap to view · disappears in {seconds}s"
                description="Placeholder shown in place of an unopened self-destructing message"
                values={{seconds: this.props.timer}} />
            </a>
          );
          attachments.length = 0;
        }
      } else if (!isSender) {
        timerBadge = <span className="timer-badge"><i className="material-icons">timer</i>
          {this.state.remaining}s</span>;
      } else {
        timerBadge = <span className="timer-badge"><i className="material-icons">timer</i>
          {this.props.timer}s</span>;
      }
    }

    // Emoji reactions folded in from separate "reaction" messages and from head.reactions.
    const reactionChips = [];
    if (this.props.reactions) {
      for (const emoji in this.props.reactions) {
        const users = this.props.reactions[emoji];
        const mine = this.props.myUserId && users.includes(this.props.myUserId);
        reactionChips.push(
          <a href="#" key={emoji} className={'reaction-chip' + (mine ? ' mine' : '')}
            onClick={this.props.onToggleReaction ? this.handleToggleReaction.bind(this, emoji) : (e => e.preventDefault())}>
            <span className="reaction-emoji">{emoji}</span>
            {users.length > 1 ? <span className="reaction-count">{users.length}</span> : null}
          </a>
        );
      }
    }

    // Undelivered message: make the failure visible instead of dropping it silently.
    const sendFailed = this.props.received == Tinode.MESSAGE_STATUS_FAILED ||
      this.props.received == Tinode.MESSAGE_STATUS_FATAL;

    return (
      <li ref={this.props.innerRef} className={sideClass}>
        {this.props.isGroup && this.props.response ?
          <div className="avatar-box">
            {fullDisplay ?
              <LetterTile
                tinode={this.props.tinode}
                topic={this.props.userFrom}
                title={this.props.userName}
                avatar={avatar} /> :
              null}
          </div> :
          null}
        <div>
          <div className={bubbleClass + (this.props.searchQuery ? ' search-hit' : '')}>
            <div className="content-meta">
              <div className={textSizeClass}>
                {content}
                {attachments}
              </div>
              {timerBadge}
              {this.props.timestamp ?
                <ReceivedMarker
                  edited={this.props.edited}
                  timestamp={this.props.timestamp}
                  received={this.props.received} />
                : null}
            </div>
            {this.props.onToggleReaction ?
              <span className="reactTrigger">
                <a href="#" onClick={this.handleTogglePicker} title="React">
                  <i className="material-icons">add_reaction</i>
                </a>
                {this.state.picker ?
                  <span className="reaction-picker">
                    {(this.props.quickReactions || []).map(emoji =>
                      <a href="#" key={emoji} onClick={this.handlePickReaction.bind(this, emoji)}>{emoji}</a>
                    )}
                  </span> : null}
              </span> : null
            }
            {this.props.showContextMenu ?
              <span className="menuTrigger">
                <a href="#" onClick={this.handleContextClick}>
                  <i className="material-icons">expand_more</i>
                </a>
              </span> : null
            }
          </div>
          {reactionChips.length > 0 ?
            <div className="reaction-bar">{reactionChips}</div> : null}
          {sendFailed ?
            <div className="send-failed">
              <i className="material-icons">error_outline</i>&nbsp;
              <FormattedMessage id="message_not_sent" defaultMessage="Not sent"
                description="Shown under a message which could not be delivered" />
              {this.props.onRetrySend ?
                <>&nbsp;· <a href="#" onClick={this.handleRetrySend}>
                  <FormattedMessage id="message_retry_send" defaultMessage="Retry"
                    description="Link to retry sending a failed message" />
                </a></> : null}
              {this.props.onDiscardSend ?
                <>&nbsp;· <a href="#" onClick={this.handleDiscardSend}>
                  <FormattedMessage id="message_discard" defaultMessage="Delete"
                    description="Link to delete a message which could not be sent" />
                </a></> : null}
            </div> : null}
          {fullDisplay ?
            <div className="author">
              {this.props.userName ||
                <i><FormattedMessage id="user_not_found" defaultMessage="Not found"
                description="In place of a user's full name when the user is not found." /></i>
              }
            </div>
            : null
          }
        </div>
      </li>
    );
  }
};

const IntlChatMessage = injectIntl(BaseChatMessage);
const ChatMessage = React.forwardRef((props, ref) => <IntlChatMessage innerRef = {ref} {...props} />);

export default ChatMessage;
